import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData, PaginationMeta } from './common';

export interface CompletionReport {
  id: string;
  projectId: string;
  repositoryId: string | null;
  planId: string | null;
  planTitle: string | null;
  title: string;
  content: string;
  commitHash: string | null;
  commitStart: string | null;
  commitEnd: string | null;
  branchName: string | null;
  pullRequestId: string | null;
  durationSeconds: number | null;
  filesModified: number | null;
  linesAdded: number | null;
  linesDeleted: number | null;
  status: string;
  qualityScore: number | null;
  reviewRecommendation?: string | null;
  reviewReason?: string | null;
  reviewStatus?: string | null;
  runnerType?: string | null;
  model?: string | null;
  contentTokenCount?: number | null;
  createdByMemberId?: string | null;
  createdByAgentId?: string | null;
  createdByType?: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind?: string | null;
  /** 작성에 사용된 도구(에이전트 설정) 이름. 행위 주체 축과 직교한다. */
  createdByAgentName?: string | null;
  createdByName?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export type CompletionReportListItem = Omit<CompletionReport, 'content'>;

type CompletionReportListResponse = {
  data: CompletionReportListItem[];
};

type CompletionReportPagedResponse = {
  data: CompletionReportListItem[];
  meta: PaginationMeta;
};

type CompletionReportResponse = {
  data: CompletionReport;
};

export const fetchCompletionReports = async (
  token: string,
  projectId: string,
  params?: {
    planId?: string;
    status?: string;
    reviewStatus?: string;
    createdByMemberId?: string;
  },
): Promise<CompletionReportListItem[]> => {
  const queryParams = new URLSearchParams();
  if (params?.planId) queryParams.set('planId', params.planId);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.reviewStatus) queryParams.set('reviewStatus', params.reviewStatus);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);

  const url = `${getApiUrl()}/api/projects/${projectId}/completion-reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch completion reports: ${response.status}`);
  }

  const data = (await response.json()) as CompletionReportListResponse;
  return data.data;
};

export const fetchCompletionReportsPaged = async (
  token: string,
  projectId: string,
  params?: {
    search?: string;
    planId?: string;
    status?: string;
    reviewStatus?: string;
    createdByMemberId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<CompletionReportListItem>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.planId) queryParams.set('planId', params.planId);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.reviewStatus) queryParams.set('reviewStatus', params.reviewStatus);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);
  if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
  if (params?.dateTo) queryParams.set('dateTo', params.dateTo);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/completion-reports${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch completion reports: ${response.status}`);
  }

  const data = (await response.json()) as CompletionReportPagedResponse;
  return data;
};

export const fetchCompletionReportById = async (
  token: string,
  projectId: string,
  id: string,
): Promise<CompletionReport> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/completion-reports/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch completion report: ${response.status}`);
  }

  const data = (await response.json()) as CompletionReportResponse;
  return data.data;
};

export const createCompletionReport = async (
  token: string,
  projectId: string,
  input: {
    planId?: string;
    repositoryId?: string;
    title: string;
    content: string;
    commitHash?: string;
    commitStart?: string;
    commitEnd?: string;
    branchName?: string;
    pullRequestId?: string;
    durationSeconds?: number;
    filesModified?: number;
    linesAdded?: number;
    linesDeleted?: number;
    status?: string;
    qualityScore?: number;
  },
): Promise<CompletionReport> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/completion-reports`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create completion report: ${response.status}`);
  }

  const data = (await response.json()) as CompletionReportResponse;
  return data.data;
};

export const updateCompletionReport = async (
  token: string,
  projectId: string,
  id: string,
  input: Partial<
    Pick<
      CompletionReport,
      | 'planId'
      | 'repositoryId'
      | 'title'
      | 'content'
      | 'commitHash'
      | 'commitStart'
      | 'commitEnd'
      | 'branchName'
      | 'pullRequestId'
      | 'durationSeconds'
      | 'filesModified'
      | 'linesAdded'
      | 'linesDeleted'
      | 'status'
      | 'qualityScore'
    >
  >,
): Promise<CompletionReport> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/completion-reports/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update completion report: ${response.status}`);
  }

  const data = (await response.json()) as CompletionReportResponse;
  return data.data;
};

export const dismissCompletionReportReview = async (
  token: string,
  projectId: string,
  id: string,
): Promise<CompletionReport> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/completion-reports/${id}/dismiss-review`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to dismiss completion report review: ${response.status}`);
  }

  const data = (await response.json()) as CompletionReportResponse;
  return data.data;
};

export const deleteCompletionReport = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/completion-reports/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete completion report: ${response.status}`);
  }
};
